import { useCallback, useEffect, useRef, useState } from 'react';
import { Armchair, BadgePercent, GlassWater, Gift, CalendarDays, BarChart3, Barcode, Boxes, Building2, Contact, LayoutDashboard, Link2, LogOut, Monitor, Moon, Package, Pill, Puzzle, Receipt, Settings, Sun, Tags, Ticket, Truck, Route, PackageCheck, FileSignature, Shirt, Bookmark, Handshake, ClipboardList, ScanBarcode, UserCog, Users, UtensilsCrossed, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import type { ModuleKey } from '@makila/pos-core';
import { api, BoError, logout, refresh, ssoLogin, type Session } from './api';
import { AuthScreen } from './AuthScreen';
import { Business, type BusinessInfo, makeCtx } from './business';
import { CategoriesPage, ProductsPage } from './CatalogPages';
import { CollectionsPage, ConsignmentPage, ReservationsPage } from './FashionPages';
import { SerialsPage, WorkOrdersPage } from './ServicesPages';
import { AgendaPage, ResourcesPage } from './AgendaPages';
import { BarPage, DoorPage } from './BarPage';
import { ContractsPage, DeliveriesReportPage, RoutesPage, VehiclesPage } from './RoutesPage';
import { AccountsPage, CustomersPage, GiftcardsPage } from './CustomersPage';
import { LabelsPage } from './LabelsPage';
import { MakilaLinked, MakilaPage, type MakilaStatus } from './makila';
import { navigate } from './navigate';
import { CommissionsPage, OperatorsPage } from './OperatorsPage';
import { PharmacyPage } from './PharmacyPage';
import { PromotionsPage } from './PromotionsPage';
import { CashSessionsPage, ReportsPage, SalesPage } from './SalesPages';
import { BusinessSetup, CompanyPage, GettingStarted, ModulesPage } from './SettingsPages';
import { StockPage } from './StockPage';
import { TablesPage } from './TablesPage';
import { StoresPage } from './StoresPage';
import { ToastProvider } from './ui';
import { PanelItem, Rail, RailItem, RailTool, SidePanel } from '../components/Rail';
import { useMedia } from '../lib/media';
import { useTheme } from '../lib/theme';
import { switchTo } from '../lib/windows';

export { navigate };

interface Page { path: string; label: string | ((t: TermsLike) => string); icon: LucideIcon; el: () => React.ReactNode; module?: ModuleKey; linkedOnly?: boolean; ownerOnly?: boolean }
interface Group { label: string; icon: LucideIcon; pages: Page[] }
type TermsLike = { items: string; staffPlural: string; customers: string };

/** O Painel abre com os "Primeiros passos" até estar tudo feito. */
function Dashboard() {
  return <><GettingStarted /><ReportsPage /></>;
}

/**
 * Primeira barra: os módulos, por ordem de importância (docs/14 §4.5) — o
 * dia, o negócio específico, o catálogo, os clientes, a equipa, as definições.
 * Cada página aparece só com o módulo dela activo; os nomes seguem o tipo de
 * negócio (Artigos / Serviços e produtos; Operadores / Profissionais).
 */
const GROUPS: Group[] = [
  { label: 'Painel', icon: LayoutDashboard, pages: [
    { path: '/gestao', label: 'Relatórios', icon: BarChart3, el: Dashboard },
    { path: '/gestao/vendas', label: 'Vendas', icon: Receipt, el: SalesPage },
    { path: '/gestao/fechos', label: 'Fechos de caixa', icon: Wallet, el: CashSessionsPage },
    { path: '/gestao/reservas', label: 'Reservas', icon: Bookmark, el: ReservationsPage, module: 'deposits' },
  ] },
  { label: 'Agenda', icon: CalendarDays, pages: [
    { path: '/gestao/agenda', label: 'Agenda', icon: CalendarDays, el: AgendaPage, module: 'agenda' },
    { path: '/gestao/salas', label: 'Salas e cadeiras', icon: Armchair, el: ResourcesPage, module: 'resources' },
  ] },
  { label: 'Mesas', icon: UtensilsCrossed, pages: [
    { path: '/gestao/mesas', label: 'Mesas', icon: UtensilsCrossed, el: TablesPage, module: 'tables' },
  ] },
  { label: 'Bar', icon: GlassWater, pages: [
    { path: '/gestao/garrafas', label: 'Garrafas e perdas', icon: GlassWater, el: BarPage, module: 'doses' },
    { path: '/gestao/entradas', label: 'Entradas e bilhetes', icon: Ticket, el: DoorPage, module: 'tickets' },
  ] },
  { label: 'Rotas', icon: Truck, pages: [
    { path: '/gestao/rotas', label: 'Rotas', icon: Route, el: RoutesPage, module: 'routes' },
    { path: '/gestao/entregas', label: 'Entregas', icon: PackageCheck, el: DeliveriesReportPage, module: 'routes' },
    { path: '/gestao/viaturas', label: 'Viaturas', icon: Truck, el: VehiclesPage, module: 'vehicles' },
    { path: '/gestao/contratos', label: 'Contratos', icon: FileSignature, el: ContractsPage, module: 'contracts' },
  ] },
  { label: 'Ordens', icon: ClipboardList, pages: [
    { path: '/gestao/ordens', label: 'Ordens de serviço', icon: ClipboardList, el: WorkOrdersPage, module: 'workorders' },
  ] },
  { label: 'Farmácia', icon: Pill, pages: [
    { path: '/gestao/farmacia', label: 'Farmácia', icon: Pill, el: PharmacyPage, module: 'prescriptions' },
  ] },
  { label: 'Catálogo', icon: Package, pages: [
    { path: '/gestao/artigos', label: (t) => t.items, icon: Package, el: ProductsPage },
    { path: '/gestao/stock', label: 'Stock', icon: Boxes, el: StockPage, module: 'stock' },
    { path: '/gestao/categorias', label: 'Categorias', icon: Tags, el: CategoriesPage },
    { path: '/gestao/colecoes', label: 'Colecções', icon: Shirt, el: CollectionsPage, module: 'collections' },
    { path: '/gestao/consignacao', label: 'Consignação', icon: Handshake, el: ConsignmentPage, module: 'consignment' },
    { path: '/gestao/promocoes', label: 'Promoções', icon: BadgePercent, el: PromotionsPage, module: 'promotions' },
    { path: '/gestao/etiquetas', label: 'Etiquetas', icon: Barcode, el: LabelsPage, module: 'labels' },
    { path: '/gestao/series', label: 'Números de série', icon: ScanBarcode, el: SerialsPage, module: 'serials' },
  ] },
  { label: 'Clientes', icon: Contact, pages: [
    { path: '/gestao/clientes', label: (t) => t.customers, icon: Contact, el: CustomersPage },
    { path: '/gestao/contas', label: 'Contas correntes', icon: Wallet, el: AccountsPage, module: 'credit' },
    { path: '/gestao/vales', label: 'Vales-oferta', icon: Gift, el: GiftcardsPage, module: 'giftcards' },
  ] },
  { label: 'Equipa', icon: Users, pages: [
    { path: '/gestao/operadores', label: (t) => t.staffPlural, icon: UserCog, el: OperatorsPage },
    { path: '/gestao/comissoes', label: 'Comissões', icon: BadgePercent, el: CommissionsPage, module: 'staff' },
  ] },
  { label: 'Definições', icon: Settings, pages: [
    { path: '/gestao/empresa', label: 'Empresa', icon: Building2, el: CompanyPage },
    { path: '/gestao/modulos', label: 'Módulos', icon: Puzzle, el: ModulesPage },
    { path: '/gestao/lojas', label: 'Lojas e caixas', icon: Monitor, el: StoresPage },
    { path: '/gestao/makila', label: 'Makila', icon: Link2, el: MakilaPage, linkedOnly: true },
  ] },
];

const ROLE = { owner: 'Dono', manager: 'Gerente', cashier: 'Operador' } as const;

/** Backoffice do Makila POS (/gestao): o dono ou gerente prepara as lojas, as caixas e o catálogo. */
export function Backoffice() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [path, setPath] = useState(location.pathname);
  const [linked, setLinked] = useState(false);
  const [info, setInfo] = useState<BusinessInfo | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [dark, toggleTheme] = useTheme();
  const wide = useMedia('(min-width: 1024px)');
  // Voltar a um módulo leva à última página aberta nele.
  const lastIn = useRef(new Map<string, string>());

  useEffect(() => {
    // "Abrir POS" no Makila: /gestao/sso#code=… — o código serve uma vez e sai logo do endereço.
    const code = location.pathname === '/gestao/sso' ? new URLSearchParams(location.hash.slice(1)).get('code') : null;
    if (code) {
      history.replaceState({}, '', '/gestao');
      setPath('/gestao');
      void ssoLogin(code).then(setSession, (e) => { setSsoError(e instanceof BoError ? e.message : 'Sem ligação à internet.'); setSession(null); });
      return;
    }
    void refresh().then(setSession);
  }, []);
  const loadBusiness = useCallback(async () => {
    try { setInfo((await api<{ business: BusinessInfo }>('/auth/me')).business); } catch { setInfo(null); }
  }, []);
  useEffect(() => {
    if (!session || session.tenant.role === 'cashier') return;
    void api<MakilaStatus>('/makila/status').then((s) => setLinked(s.linked), () => setLinked(false));
    void loadBusiness();
  }, [session, loadBusiness]);
  useEffect(() => {
    const on = () => setPath(location.pathname);
    addEventListener('popstate', on);
    return () => removeEventListener('popstate', on);
  }, []);

  if (session === undefined) return <div className="grid h-full place-items-center text-muted">A abrir…</div>;
  if (!session) return <ToastProvider><AuthScreen onSession={setSession} notice={ssoError ? `Entrada pelo Makila: ${ssoError}` : null} /></ToastProvider>;
  if (session.tenant.role === 'cashier') {
    return <div className="grid h-full place-items-center p-6 text-center">O seu perfil não tem acesso à gestão. Use a caixa.</div>;
  }

  const ctx = makeCtx(info, loadBusiness);
  const terms = ctx.type.terms;
  const label = (p: Page) => (typeof p.label === 'function' ? p.label(terms) : p.label);
  const groups = GROUPS
    .map((g) => ({ ...g, pages: g.pages.filter((p) => (!p.module || ctx.has(p.module)) && (!p.linkedOnly || linked)) }))
    .filter((g) => g.pages.length);
  const here = path.replace(/\/$/, '') || '/gestao';
  const group = groups.find((g) => g.pages.some((p) => p.path === here)) ?? groups[0]!;
  const page = group.pages.find((p) => p.path === here) ?? group.pages[0]!;
  lastIn.current.set(group.label, page.path);
  const Page = page.el;
  const exit = async () => { await logout(); setSession(null); };
  // Empresa vinda do Makila sem tipo de negócio: primeiro escolhe-se (registo, passo 3).
  const setup = info && !info.onboarded && session.tenant.role === 'owner';

  return (
    <ToastProvider>
      <Business.Provider value={ctx}>
        <div className="flex h-full flex-col-reverse md:flex-row">
          <Rail label="Gestão" footer={<>
            <RailItem icon={Monitor} label="Caixa" ariaLabel="Ir para a caixa" onClick={() => switchTo('/')} />
            <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
            <RailTool icon={LogOut} label="Sair" onClick={() => void exit()} />
          </>}>
            {!setup && groups.map((g) => (
              <RailItem key={g.label} icon={g.icon} label={g.label} active={g === group} href={g.pages[0]!.path}
                onClick={() => navigate(lastIn.current.get(g.label) ?? g.pages[0]!.path)} />
            ))}
          </Rail>

          {wide && !setup && (
            <SidePanel title={group.label} subtitle={session.tenant.name} footer={
              <div className="flex items-center gap-3 px-1">
                <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink text-sm font-bold text-white dark:bg-white dark:text-ink">{session.user.name.slice(0, 1).toUpperCase()}</span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{session.user.name}</div>
                  <div className="truncate text-xs text-muted">{ROLE[session.tenant.role]} · {ctx.type.name}</div>
                </div>
              </div>
            }>
              <div className="space-y-0.5">
                {group.pages.map((p) => <PanelItem key={p.path} href={p.path} icon={p.icon} active={p === page} onClick={() => navigate(p.path)}>{label(p)}</PanelItem>)}
              </div>
              <button onClick={() => switchTo('/')} className="mt-6 flex w-full items-center gap-3 rounded-xl border border-dashed border-base p-3 text-left text-sm hover:border-brand-500 hover:bg-surface-2">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-700 text-white"><Monitor size={18} /></span>
                <span><span className="block font-semibold">Ir para a caixa</span><span className="text-xs text-muted">Abre ao lado, sem fechar a gestão</span></span>
              </button>
            </SidePanel>
          )}

          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
            {!wide && !setup && group.pages.length > 1 && (
              <nav aria-label={group.label} className="flex shrink-0 gap-1 overflow-x-auto border-b border-base bg-[var(--surface)] px-3 py-2">
                {group.pages.map((p) => (
                  <a key={p.path} href={p.path} onClick={(e) => { e.preventDefault(); navigate(p.path); }} aria-current={p === page ? 'page' : undefined}
                    className={clsx('flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-medium', p === page ? 'bg-brand-700 text-white' : 'text-muted hover:bg-surface-2')}>
                    <p.icon size={16} /> {label(p)}
                  </a>
                ))}
              </nav>
            )}
            <div className="w-full p-4 md:p-8">
              {setup ? <BusinessSetup /> : (
                <>
                  <header className="mb-6">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted">{group.label === label(page) ? session.tenant.name : group.label}</div>
                    <h1 className="mt-1 font-display text-2xl font-bold md:text-3xl">{label(page)}</h1>
                  </header>
                  <MakilaLinked.Provider value={linked}><Page /></MakilaLinked.Provider>
                </>
              )}
            </div>
          </main>
        </div>
      </Business.Provider>
    </ToastProvider>
  );
}
